import { useMemo } from "react";

import {
  type KeystoneOfferEligibility,
  type OfferStatusCode,
} from "data/keystone-api-mapping";
import { usePolicyDetail, usePolicyOffers } from "data/ss-data-hooks";

const ENROLLED_STATUSES: OfferStatusCode[] = ["ENROLLED", "ORDERED"];

const IMPLEMENTED_OFFERING_NAMES = [
  "leakbot",
  "ting-offer",
  "wind-defense",
  "ting-required",
];

const isImplemented = (offer: KeystoneOfferEligibility) =>
  IMPLEMENTED_OFFERING_NAMES.includes(offer.offeringInternalName);

const isEnrolled = (offer: KeystoneOfferEligibility) =>
  !!offer.offerStatusCode && ENROLLED_STATUSES.includes(offer.offerStatusCode);

export const useEligibleOffers = (policyId?: string) => {
  // `null` skips the fetch, keeping the opt-out for callers with no policy.
  const {
    data: rawOffers,
    isLoading: offersLoading,
    error,
  } = usePolicyOffers(policyId ?? null);
  const { data, isLoading: enrollmentLoading } = usePolicyDetail(policyId);
  const isEnrolledInLeakBot = data?.userDetails.isEnrolledInLeakBot;

  const eligibleOffers = useMemo(() => {
    const offers = [...(rawOffers ?? [])];
    if (policyId && isEnrolledInLeakBot) {
      offers.push({
        isFeatured: false,
        insightPolicyId: policyId,
        isEligible: true,
        offerId: null,
        offerStatusCode: "ENROLLED",
        offerUrl: "",
        offeringId: "",
        offeringInternalName: "leakbot",
        offeringMarketingName: "LeakBot",
        additionalDetails: [],
      });
    }

    return {
      allOffers: offers.filter(
        (offer) => offer.isEligible && isImplemented(offer),
      ),
      enrolledOffers: offers.filter(
        (offer) => isEnrolled(offer) && isImplemented(offer),
      ),
      unenrolledOffers: offers.filter(
        (offer) =>
          offer.isEligible && !isEnrolled(offer) && isImplemented(offer),
      ),
    };
  }, [rawOffers, policyId, isEnrolledInLeakBot]);

  return {
    eligibleOffers,
    loading: !policyId || offersLoading || enrollmentLoading,
    error,
  };
};
